import React, { useEffect, useState } from 'react';
import BrandLogo from './BrandLogo';
import SalonForm from '../components/Form/TuMesaYaForm';
import salonService from '../assets/salon-service.webp';
import barberService from '../assets/barber-service.webp';
import { FormData } from '../types';
import { submitLeadToWebhook } from '../utils/api';
import { useTracking } from '../hooks/useTracking';
import { LOGIN_URL, REGISTER_URL } from '../config/appLinks';

type Channel = 'phone' | 'whatsapp';
type SubmitState = 'idle' | 'success' | 'error';

const serviceRows = [
  {
    time: '09:30',
    service: 'Corte + peinado',
    duration: '60 min',
    color: 'blue',
  },
  {
    time: '11:00',
    service: 'Corte + barba',
    duration: '45 min',
    color: 'orange',
  },
  {
    time: '12:15',
    service: 'Color y tratamiento',
    duration: '120 min',
    color: 'lime',
  },
  { time: '16:30', service: 'Corte', duration: '30 min', color: 'cream' },
];

const LandingPage: React.FC = () => {
  const [activeChannel, setActiveChannel] = useState<Channel>('phone');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [hideStickyCta, setHideStickyCta] = useState(false);
  const { trackCTAClick, trackPageView } = useTracking();

  useEffect(() => {
    trackPageView('tusalonya', 'organic');

    const revealObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    document.querySelectorAll('[data-reveal]').forEach(element => {
      revealObserver.observe(element);
    });

    const demo = document.getElementById('demo');
    const stickyObserver = new IntersectionObserver(
      entries => setHideStickyCta(entries.some(entry => entry.isIntersecting)),
      { threshold: 0.08 }
    );

    if (demo) stickyObserver.observe(demo);

    return () => {
      revealObserver.disconnect();
      stickyObserver.disconnect();
    };
  }, [trackPageView]);

  const handleCta = (source: string) => {
    trackCTAClick('solicitar-demo', source);
  };

  const handleFormSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    setSubmitState('idle');

    const response = await submitLeadToWebhook({
      ...formData,
      salon: formData.restaurant,
      brand: 'Peluqueria.AI',
      vertical: 'hair-beauty',
      source: 'tusalonya-demo',
      timestamp: new Date().toISOString(),
    });

    setIsSubmitting(false);
    setSubmitState(response.success ? 'success' : 'error');
    return response.success;
  };

  return (
    <div className="salon-site">
      <header className="salon-header">
        <a href="#inicio" className="salon-header__logo">
          <BrandLogo inverse compact />
        </a>
        <nav aria-label="Navegación principal">
          <a href="#ritmo">Cómo funciona</a>
          <a href="#servicio">Durante el servicio</a>
          <a href="#menos-huecos">Menos huecos</a>
        </nav>
        <div className="salon-header__actions">
          <a
            className="salon-header__login"
            href={LOGIN_URL}
            onClick={() => handleCta('header-login')}
          >
            Acceder
          </a>
          <a
            className="salon-button salon-button--header"
            href={REGISTER_URL}
            onClick={() => handleCta('header-alta')}
          >
            Crear cuenta <span>↗</span>
          </a>
        </div>
      </header>

      <main>
        <section className="salon-hero" id="inicio">
          <div className="salon-hero__copy" data-reveal>
            <span className="salon-kicker">
              <i /> Agenda en movimiento · 24/7
            </span>
            <h1>
              Tú cambias estilos.
              <span>Peluqueria.AI mueve la agenda.</span>
            </h1>
            <p>
              Atiende las llamadas, encuentra el hueco correcto según el
              servicio y confirma citas incluso cuando todo el equipo tiene las
              manos ocupadas. La atención por WhatsApp llegará próximamente.
            </p>
            <div className="salon-hero__actions">
              <a
                href={REGISTER_URL}
                className="salon-button"
                onClick={() => handleCta('hero-alta')}
              >
                Crear mi cuenta <span>↗</span>
              </a>
              <a
                className="salon-play"
                href="#demo"
                onClick={() => handleCta('hero-demo')}
              >
                <span>▶</span> Prefiero ver una demo
              </a>
            </div>
          </div>

          <div className="salon-collage" data-reveal>
            <figure className="salon-photo salon-photo--first">
              <img
                src={salonService}
                alt="Peluquera atendiendo a una clienta en un salón contemporáneo"
              />
              <figcaption>Salón · corte y color</figcaption>
            </figure>
            <figure className="salon-photo salon-photo--second">
              <img
                src={barberService}
                alt="Barbero atendiendo a un cliente en un estudio unisex"
              />
              <figcaption>Barbería · corte y barba</figcaption>
            </figure>
            <div className="floating-booking coming-soon">
              <p className="coming-soon__band coming-soon__band--tag">
                Próximamente
              </p>
              <div className="floating-booking__body coming-soon__preview">
                <span className="floating-booking__icon">✓</span>
                <div>
                  <small>Ejemplo de cita</small>
                  <strong>Viernes · 18:00</strong>
                  <em>Corte + peinado · WhatsApp</em>
                </div>
              </div>
            </div>
            <div className="floating-call">
              <span>☎</span>
              <small>Atendiendo llamada</small>
              <i />
              <i />
              <i />
            </div>
          </div>
        </section>

        <section className="kinetic-strip" aria-label="Funciones principales">
          <div>
            <span>LLAMADAS SIN ESPERA</span>
            <b>✦</b>
            <span>WHATSAPP · PRÓXIMAMENTE</span>
            <b>✦</b>
            <span>CITAS 24/7</span>
            <b>✦</b>
            <span>RECORDATORIOS</span>
            <b>✦</b>
            <span>CANCELACIONES</span>
            <b>✦</b>
          </div>
        </section>

        <section className="rhythm-section" id="ritmo">
          <div className="rhythm-heading" data-reveal>
            <span className="salon-label">
              01 · CADA SERVICIO TIENE SU RITMO
            </span>
            <h2>
              No es solo encontrar un hueco. Es encontrar el hueco correcto.
            </h2>
            <p>
              Un corte, un color o un arreglo de barba necesitan tiempos,
              profesionales y recursos distintos. Peluqueria.AI conversa con el
              cliente y organiza cada cita sin convertirla en un formulario.
            </p>
          </div>

          <div className="schedule-stage" data-reveal>
            <div className="schedule-stage__top">
              <span>Viernes 24</span>
              <div>
                <button type="button">‹</button>
                <button type="button">›</button>
              </div>
            </div>
            <div className="schedule-grid">
              {serviceRows.map(row => (
                <div
                  className="schedule-row"
                  key={`${row.time}-${row.service}`}
                >
                  <time>{row.time}</time>
                  <span
                    className={`schedule-service schedule-service--${row.color}`}
                  >
                    <strong>{row.service}</strong>
                    <small>{row.duration}</small>
                  </span>
                </div>
              ))}
            </div>
            <div className="schedule-incoming">
              <span>+</span>
              <p>
                “Quería corte y barba el viernes por la tarde.”
                <small>Hueco compatible encontrado · 18:45</small>
              </p>
            </div>
          </div>

          <ol className="rhythm-steps">
            <li data-reveal>
              <span>01</span>
              <strong>Pregunta qué necesita</strong>
              <p>Servicio, profesional y preferencias.</p>
            </li>
            <li data-reveal>
              <span>02</span>
              <strong>Calcula el tiempo real</strong>
              <p>Según corte, color, tratamiento o barba.</p>
            </li>
            <li data-reveal>
              <span>03</span>
              <strong>Confirma sin fricción</strong>
              <p>La cita entra y el cliente recibe los datos.</p>
            </li>
          </ol>
        </section>

        <section className="hands-section" id="servicio">
          <div className="hands-copy" data-reveal>
            <span className="salon-label salon-label--orange">
              02 · MANOS OCUPADAS
            </span>
            <h2>
              Cuando estás creando, no puedes parar para coger el teléfono.
            </h2>
            <p>
              Entre tijeras, secadores, color y máquinas, una llamada siempre
              llega en mal momento. Peluqueria.AI la atiende mientras el servicio
              continúa.
            </p>
            <div className="hands-stats">
              <span>
                <strong>0</strong>
                <small>servicios interrumpidos</small>
              </span>
              <span>
                <strong>1</strong>
                <small>canal atendiendo hoy · WhatsApp, próximamente</small>
              </span>
            </div>
          </div>
          <div className="hands-gallery" data-reveal>
            <figure>
              <img src={salonService} alt="Servicio de peluquería en marcha" />
            </figure>
            <figure>
              <img src={barberService} alt="Servicio de barbería en marcha" />
            </figure>
            <div className="call-caught">
              <span>☎</span>
              <div>
                <small>Llamada recogida en 2 s</small>
                <strong>“¿Tenéis hueco para esta tarde?”</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="channels-section">
          <div className="channels-heading" data-reveal>
            <span className="salon-label">
              03 · UNA PUERTA ABIERTA, OTRA EN CAMINO
            </span>
            <h2>La conversación cambia. La operativa no.</h2>
            <p>
              Hoy Peluqueria.AI atiende el teléfono. La atención por WhatsApp
              todavía no está disponible: lo que ves en esa pestaña es una
              muestra de cómo funcionará.
            </p>
          </div>
          <div className="channel-switcher" data-reveal>
            <div className="channel-switcher__tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeChannel === 'phone'}
                className={activeChannel === 'phone' ? 'is-active' : ''}
                onClick={() => setActiveChannel('phone')}
              >
                Llamada
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeChannel === 'whatsapp'}
                className={activeChannel === 'whatsapp' ? 'is-active' : ''}
                onClick={() => setActiveChannel('whatsapp')}
              >
                WhatsApp
                <span className="tab-soon">Próximamente</span>
              </button>
            </div>
            {activeChannel === 'phone' ? (
              <div className="channel-conversation" key="phone">
                <div className="channel-meta">
                  <span>☎ Llamada en curso</span>
                  <time>00:38</time>
                </div>
                <blockquote>
                  <small>Cliente</small>
                  “Necesito un cambio de color. ¿Tenéis hueco el sábado?”
                </blockquote>
                <blockquote className="channel-conversation__answer">
                  <small>Peluqueria.AI</small>
                  “Sí. Para color y tratamiento tengo disponibilidad a las 10:30
                  con Alex.”
                </blockquote>
                <div className="channel-result">
                  <span>✓</span>
                  <p>
                    <small>Cita confirmada</small>
                    <strong>Sábado · 10:30 · Color + tratamiento</strong>
                  </p>
                </div>
              </div>
            ) : (
              <div className="coming-soon" key="whatsapp">
                <p className="coming-soon__band">Próximamente</p>
                <div className="coming-soon__preview">
                  <div className="channel-conversation">
                    <div className="channel-meta">
                      <span className="wa-badge" aria-label="WhatsApp">
                  WA
                </span>
                      <time>19:42</time>
                    </div>
                    <blockquote>
                      <small>Cliente</small>
                      “Hola, ¿podría reservar corte y barba para mañana?”
                    </blockquote>
                    <blockquote className="channel-conversation__answer">
                      <small>Peluqueria.AI</small>
                      “Claro. Te puedo ofrecer las 17:15 o las 18:45.”
                    </blockquote>
                    <div className="channel-result">
                      <span>✓</span>
                      <p>
                        <small>Cita confirmada</small>
                        <strong>Mañana · 18:45 · Corte + barba</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="after-dark-section">
          <div className="after-dark-art" data-reveal>
            <div className="time-orbit">
              <span>23:17</span>
              <i />
              <b />
            </div>
            <div className="late-card late-card--one coming-soon">
              <p className="coming-soon__band coming-soon__band--tag">
                Próximamente
              </p>
              <div className="coming-soon__preview">
                <small>WhatsApp · ejemplo</small>
                <strong>“¿Abrís los lunes?”</strong>
                <span>Así responderá cuando esté disponible</span>
              </div>
            </div>
            <div className="late-card late-card--two">
              <small>Nueva cita</small>
              <strong>Martes · 17:30</strong>
              <span>Corte · 30 min</span>
            </div>
          </div>
          <div className="after-dark-copy" data-reveal>
            <span className="salon-label salon-label--lime">
              04 · CUANDO EL LOCAL CIERRA
            </span>
            <h2>La persiana baja. La agenda sigue abierta.</h2>
            <p>
              Peluqueria.AI informa, encuentra huecos y confirma citas por la noche,
              en días de descanso y durante las vacaciones.
            </p>
          </div>
        </section>

        <section className="no-gap-section" id="menos-huecos">
          <div className="no-gap-copy" data-reveal>
            <span className="salon-label">05 · ANTES DE LA CITA</span>
            <h2>Un recordatorio a tiempo puede salvar un hueco.</h2>
            <p>
              Muy pronto el cliente podrá confirmar o cancelar desde WhatsApp:
              si no puede venir, la cita se liberará antes y volverá a estar
              disponible. Los recordatorios por WhatsApp todavía no están
              activos.
            </p>
            <div className="flow-line">
              <span>Recordatorio</span>
              <i />
              <span>Respuesta</span>
              <i />
              <span>Agenda</span>
            </div>
          </div>
          <div
            className="message-device coming-soon coming-soon--edge"
            data-reveal
          >
            <p className="coming-soon__band coming-soon__band--edge">
              Próximamente
            </p>
            <div className="coming-soon__preview">
              <div className="message-device__top">
                <span className="wa-badge" aria-label="WhatsApp">
                  WA
                </span>
                <p>
                  <strong>Peluqueria.AI</strong>
                  <small>
                    Recordatorio por WhatsApp
                    <span className="tab-soon">Próximamente</span>
                  </small>
                </p>
              </div>
              <div className="message-bubble">
                <p>
                  Hola, Dani. Te recordamos tu cita de corte mañana a las
                  17:30.
                </p>
                <div className="message-actions">
                  <button type="button" disabled>
                    ✓ Confirmar
                  </button>
                  <button type="button" disabled>
                    Cancelar cita
                  </button>
                </div>
                <small>19:00 ✓✓</small>
              </div>
            </div>
            <p className="message-device__note">
              Maqueta de ejemplo. Los recordatorios por WhatsApp todavía no
              están disponibles, así que estos botones no funcionan.
            </p>
          </div>
        </section>

        <section className="service-spectrum">
          <div className="spectrum-heading" data-reveal>
            <span className="salon-label">06 · TU FORMA DE TRABAJAR</span>
            <h2>Una agenda que entiende la diferencia entre cada servicio.</h2>
          </div>
          <div className="spectrum-grid">
            <article className="spectrum-card spectrum-card--blue" data-reveal>
              <span>30'</span>
              <strong>Corte</strong>
              <p>Profesional, duración y disponibilidad.</p>
            </article>
            <article
              className="spectrum-card spectrum-card--orange"
              data-reveal
            >
              <span>45'</span>
              <strong>Corte + barba</strong>
              <p>Dos servicios encajados en el mismo bloque.</p>
            </article>
            <article className="spectrum-card spectrum-card--lime" data-reveal>
              <span>120'</span>
              <strong>Color</strong>
              <p>Tiempo suficiente y profesional adecuado.</p>
            </article>
            <article className="spectrum-card spectrum-card--cream" data-reveal>
              <span>60'</span>
              <strong>Tratamiento</strong>
              <p>Información previa y cita confirmada.</p>
            </article>
          </div>
        </section>

        <section className="demo-salon-section" id="demo">
          <div className="demo-salon-copy" data-reveal>
            <BrandLogo inverse />
            <span className="salon-label salon-label--lime">
              PON TU AGENDA EN MOVIMIENTO
            </span>
            <h2>Probemos con los servicios reales de tu negocio.</h2>
            <p>
              Prepararemos una demo con tus horarios, servicios, profesionales y
              preguntas habituales.
            </p>
            <ul>
              <li>
                Atención telefónica en la demostración. WhatsApp
                <span className="tab-soon">Próximamente</span>
              </li>
              <li>Adaptada a peluquería, barbería o salón unisex.</li>
              <li>Sin compromiso.</li>
            </ul>
          </div>
          <div className="demo-salon-form" data-reveal>
            {submitState === 'success' ? (
              <div className="salon-form-success" role="status">
                <span>✓</span>
                <h3>Solicitud recibida.</h3>
                <p>Te llamaremos para preparar una demo a medida.</p>
              </div>
            ) : (
              <>
                <div className="demo-salon-form__heading">
                  <small>Cuéntanos cómo trabajáis</small>
                  <strong>Montamos la demo con vuestro ritmo.</strong>
                </div>
                <SalonForm
                  onSubmit={handleFormSubmit}
                  isSubmitting={isSubmitting}
                  variant="demo"
                  compact
                  submitLabel="Solicitar una demo"
                />
                {submitState === 'error' && (
                  <p className="salon-form-error" role="alert">
                    No hemos podido enviar la solicitud. Inténtalo de nuevo.
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <footer className="salon-footer">
        <BrandLogo inverse />
        <p>Teléfono, con citas atendidas 24/7. WhatsApp, próximamente.</p>
        <nav>
          <a href="#ritmo">Cómo funciona</a>
          <a href="#menos-huecos">Recordatorios</a>
          <a href="#demo">Solicitar demo</a>
          <a href={LOGIN_URL}>Acceder</a>
          <a href={REGISTER_URL}>Crear cuenta</a>
        </nav>
        <nav className="salon-footer__legal">
          <a href="/aviso-legal">Aviso legal</a>
          <a href="/terminos">Términos y condiciones</a>
          <a href="/politica-privacidad">Política de privacidad</a>
          <a href="/cookies">Política de cookies</a>
          <button type="button" data-cc="show-preferencesModal">
            Configurar cookies
          </button>
        </nav>
        <small>© {new Date().getFullYear()} Peluqueria.AI</small>
      </footer>

      <a
        href={REGISTER_URL}
        className={`salon-sticky-demo${hideStickyCta ? ' is-hidden' : ''}`}
        onClick={() => handleCta('sticky-mobile-alta')}
      >
        Crear mi cuenta <span>↗</span>
      </a>
    </div>
  );
};

export default LandingPage;
